import { Fragment } from 'react'
import { Dialog, DialogPanel, Transition } from '@headlessui/react'

interface BookingSuccessModalProps {
  open: boolean
  onClose: () => void
  onNavigateToCalendar?: () => void
  professionalName: string
  sessionDetails: {
    date: string
    time: string
    duration: number
  }
  amount: number
}

export default function BookingSuccessModal({
  open,
  onClose,
  onNavigateToCalendar,
  professionalName,
  sessionDetails,
  amount
}: BookingSuccessModalProps) {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[130]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px) saturate(120%)' }} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.985]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.985]"
            >
              <DialogPanel
                className="w-full max-w-lg overflow-hidden rounded-2xl md:rounded-[20px]"
                style={{ background: 'hsl(0 0% 5%)', border: '1px solid hsl(0 0% 20%)', boxShadow: '0 30px 80px rgba(0,0,0,0.55)' }}
              >
                <div className="p-10 text-center">
                  {/* Checkmark icon */}
                  <div
                    className="mx-auto w-14 h-14 rounded-full grid place-items-center mb-5"
                    style={{ background: 'rgba(255,255,255,0.05)', boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                      <path d="M4 12l5 5L20 6" />
                    </svg>
                  </div>

                  {/* Title */}
                  <h3 className="text-[28px] font-medium tracking-tight">
                    You're <span className="font-serif italic font-normal">booked</span>
                  </h3>

                  {/* Confirmation text */}
                  <p className="text-white/50 text-[13.5px] mt-3 max-w-md mx-auto leading-relaxed">
                    Your session with {professionalName} is on the calendar for {sessionDetails.date} at {sessionDetails.time}. We've sent a confirmation and the meeting link to your email.
                  </p>

                  {/* Session details card */}
                  <div className="mt-6 panel-soft p-5 text-left">
                    <div className="summary-row">
                      <span className="summary-lbl">WITH</span>
                      <span className="font-medium">{professionalName}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-lbl">DATE</span>
                      <span>{sessionDetails.date}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-lbl">TIME</span>
                      <span>{sessionDetails.time}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-lbl">SESSION</span>
                      <span>30-min · video call</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-lbl">PAID</span>
                      <span className="font-semibold">${amount.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-center gap-2 mt-7">
                    {onNavigateToCalendar && (
                      <button
                        className="btn btn-outline"
                        onClick={() => {
                          onNavigateToCalendar()
                          onClose()
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M8 3v4M16 3v4M3.5 10h17"/></svg>
                        View calendar
                      </button>
                    )}
                    <button className="btn btn-primary" onClick={onClose}>Done</button>
                  </div>

                  {/* Footer */}
                  <p className="text-white/30 text-[11px] mt-6">
                    You'll receive a reminder 15 minutes before your session.
                  </p>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
